import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import logo from '../assets/EngagedMla.png';
import registerHero from '../assets/register-hero.png';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CSRFToken from '../security/CSRFToken';
import { sanitizeText, validateEmail, sanitizeObject } from '../security/sanitize';
import { getContextualAuthErrorMessage } from '../utils/authErrorHandler';
import { useFeedback } from '../contexts/FeedbackContext';

// Icons
import { AlertCircle, Star, Hash, Camera, ShoppingBag, Phone, MessageCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    userType: 'influencer' // Default to influencer
  });
  const [error, setError] = useState('');
  const { signup } = useAuth();
  const { handleAsyncOperation } = useFeedback();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setError('Security validation failed. Please refresh the page and try again.');
      return;
    }
    
    // Sanitize form data
    const sanitizedData = sanitizeObject(formData);
    const validEmail = validateEmail(sanitizedData.email);
    
    // Validate form
    if (!validEmail || !sanitizedData.password || !sanitizedData.displayName) {
      setError('Please fill in all required fields with valid information');
      return;
    }
    
    if (sanitizedData.password !== sanitizedData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (sanitizedData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setError('');
    
    const operation = async () => {
      
      // Development mode registration - bypass Firebase authentication
      const isDevelopment = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_FIREBASE === 'true';
      
      if (isDevelopment) {
        // Simulate successful registration for development

        // Wait for a moment to simulate network request
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Set mock user in localStorage for development
        localStorage.setItem('devModeUser', JSON.stringify({
          uid: 'dev-user-' + Date.now(),
          email: formData.email,
          displayName: formData.displayName,
          userType: formData.userType,
          emailVerified: false
        }));
        
        // Store user type in localStorage for the verification page
        localStorage.setItem('userType', formData.userType);
        
        // Redirect to email verification page
        navigate('/email-verification');
      } else {
        // Real Firebase registration for production
        await signup(
          sanitizedData.email,
          sanitizedData.password,
          sanitizedData.displayName,
          sanitizedData.userType || 'influencer',
          {
            // Pass any additional data that might be needed
            socialMedia: sanitizedData.socialMedia || {},
            businessName: sanitizedData.businessName || '',
            website: sanitizedData.website || ''
          }
        );
        
        // Redirect to email verification page
        navigate('/email-verification');
        return { success: true };
      }
    };

    try {
      await handleAsyncOperation(
        operation,
        {
          title: 'Creating Account...',
          message: 'Please wait while we set up your influencer account.'
        },
        {
          title: 'Account Created!',
          message: 'Your account has been created successfully. Please verify your email.',
          autoCloseDelay: 2000
        },
        {
          title: 'Registration Failed',
          message: 'Unable to create account. Please try again.'
        }
      );
    } catch (error) {
      console.error('Registration error:', error);
      const userFriendlyMessage = getContextualAuthErrorMessage(error, 'registration');
      setError(userFriendlyMessage);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <div className="flex flex-1 min-h-[calc(100vh-64px)]">
        {/* Left column — form */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
          {/* Artistic background overlay */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Large soft gradient orb */}
            <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-primary/25 to-blue-300/20 blur-3xl"></div>
            {/* Smaller accent orb */}
            <div className="absolute bottom-10 -left-20 w-64 h-64 rounded-full bg-gradient-to-tr from-rose-200/25 to-primary/15 blur-2xl"></div>
            {/* Diagonal line accent */}
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.08]" style={{
              backgroundImage: "repeating-linear-gradient(135deg, #3B5BA5 0px, #3B5BA5 1px, transparent 1px, transparent 60px)",
            }}></div>
            {/* Floating geometric shapes */}
            <div className="absolute top-1/4 right-12 w-20 h-20 border border-primary/25 rounded-2xl rotate-12"></div>
            <div className="absolute bottom-1/3 right-24 w-12 h-12 border-2 border-blue-300/30 rounded-full"></div>
            <div className="absolute top-1/2 right-8 w-3 h-3 bg-primary/40 rounded-full"></div>
          </div>

          <div className="relative w-full max-w-md mx-auto">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Create your account
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Or{' '}
              <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
                sign in to your existing account
              </Link>
            </p>

            <div className="mt-8 bg-white">
              {/* Account Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                <div className="flex space-x-4">
                  <div className="flex items-center">
                    <input
                      id="account-influencer"
                      name="userType"
                      type="radio"
                      value="influencer"
                      checked={formData.userType === 'influencer'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label htmlFor="account-influencer" className="ml-2 block text-sm text-gray-700">
                      Influencer
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="account-brand"
                      name="userType"
                      type="radio"
                      value="brand"
                      checked={formData.userType === 'brand'}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label htmlFor="account-brand" className="ml-2 block text-sm text-gray-700">
                      Brand
                    </label>
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit}>
                <CSRFToken />
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    {formData.userType === 'brand' ? 'Company Name' : 'Full Name'}
                  </label>
                  <div className="mt-1">
                    <input
                      id="displayName"
                      name="displayName"
                      type="text"
                      autoComplete="name"
                      required
                      value={formData.displayName}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="terms"
                    name="terms"
                    type="checkbox"
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                    I agree to the{' '}
                    <Link to="/terms-of-service" className="font-medium text-primary hover:text-primary-dark">
                      Terms and Conditions
                    </Link>
                  </label>
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Create account
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right column — image */}
        <div className="hidden lg:flex flex-1 relative overflow-hidden">
          <img
            src={registerHero}
            alt="Join the Engaged Manila community"
            className="absolute left-0 w-full object-cover"
            style={{ top: "32px", height: "calc(100% - 32px)" }}
          />
          {/* Overlay gradient */}
          <div
            className="absolute left-0 w-full"
            style={{
              top: "32px",
              height: "calc(100% - 32px)",
              background: "linear-gradient(135deg, rgba(59,91,165,0.7) 0%, rgba(59,91,165,0.3) 50%, rgba(230,57,70,0.2) 100%)",
            }}
          />
          {/* Content overlay */}
          <div className="relative z-10 flex flex-col justify-end p-12 text-white">
            <h3 className="text-3xl font-bold leading-tight">
              Join the<br />Engaged Manila community
            </h3>
            <p className="mt-3 text-white/80 max-w-sm">
              Connect with brands that fit your content, get paid for authentic reviews, and track your growth with real analytics.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;
